import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { agentInstanceService } from '../../../services/agentService';
import { voiceConfigService } from '../../../services/voiceConfigService';
import { fileService } from '../../../services/fileService';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Checkbox, SelectField, TextArea, TextField } from '../../ui/Field';
import { Surface } from '../../ui/Surface';

type TtsService = 'twilio' | 'elevenlabs' | 'supertonic' | 'fish';

/** Carrier code — the server's provider registry decides which exist (twilio, telnyx, plivo, …). */
type TelephonyProvider = string;

/** Twilio/Telnyx keep their per-agent credential fields; every other carrier's credentials are
 *  configured once per tenant (Settings → Voice), so the agent only picks the carrier. */
const AGENT_LEVEL_CREDENTIAL_PROVIDERS = new Set(['twilio', 'telnyx']);

/** No carrier chosen for this agent: calls use the client's carrier (Settings → Voice). Saved as
 *  an agent `telephonyConfig` WITHOUT `provider`, which the runtime resolves to the tenant's
 *  carrier. The default, so saving this tab never pins an agent to a carrier by accident. */
const USE_TENANT_CARRIER = '';

interface VoiceConfig {
  provider: TelephonyProvider;
  accountSid: string;
  authToken: string;
  telnyxApiKey: string;
  phoneNumber: string;
  voiceConfig: {
    voice: string;
    language: string;
    gatherTimeoutSeconds: number;
    speechPauseTimeoutSeconds?: number;
    timeout?: number;
    speechTimeoutSeconds?: number;
    ttsService: TtsService;
    idleMessages?: string[];
    idleTimeoutSeconds?: number;
    idleMaxPrompts?: number;
    idleGoodbyeMessage?: string;
    enableHoldMusic?: boolean;
    enableAmbientMusic?: boolean;
    ambientMusicKey?: string;
    ambientMusicBucket?: string;
    keyterms?: string;
  };
  welcomeMessage: string;
}

interface Props {
  agent: any;
}

// Twilio built-in voices — value is passed directly to Twilio <Say voice="...">
const TWILIO_VOICE_OPTIONS = [
  { value: 'Polly.Joanna-Generative', label: 'Joanna (US Female, Generative)' },
  { value: 'Polly.Matthew-Generative', label: 'Matthew (US Male, Generative)' },
  { value: 'Polly.Kajal-Generative', label: 'Kajal (Indian English, Generative)' },
  { value: 'Polly.Lupe-Generative', label: 'Lupe (Spanish/LatAm, Generative)' },
  { value: 'Polly.Pedro-Generative', label: 'Pedro (Spanish/LatAm, Generative)' },
  { value: 'Polly.Ayanda-Generative', label: 'Ayanda (South African, Generative)' },
  { value: 'Polly.Ruth-Neural', label: 'Ruth (US Female, Neural)' },
  { value: 'Polly.Amy-Neural', label: 'Amy (UK Female, Neural)' },
  { value: 'Polly.Stephen-Neural', label: 'Stephen (US Male, Neural)' },
  { value: 'Polly.Matthew-Neural', label: 'Matthew (US Male, Neural)' },
  { value: 'alice', label: 'Alice (Default)' },
];

const LANGUAGE_OPTIONS = [
  'en-US', 'en-IN', 'en-GB', 'es-US', 'es-ES', 'fr-FR', 'de-DE', 'hi-IN',
];

const DEFAULT_VOICE: Record<TtsService, string> = {
  twilio: 'Polly.Joanna-Generative',
  elevenlabs: '21m00Tcm4TlvDq8ikWAM', // Rachel — fallback until voices load
  supertonic: 'F1',
  fish: '', // empty = Fish default voice; paste a reference_id to pick one
};

// Supertonic (self-hosted) preset voices. F* female, M* male.
const SUPERTONIC_VOICE_OPTIONS = [
  { value: 'F1', label: 'F1 (Female)' },
  { value: 'F2', label: 'F2 (Female)' },
  { value: 'F3', label: 'F3 (Female)' },
  { value: 'F4', label: 'F4 (Female)' },
  { value: 'F5', label: 'F5 (Female)' },
  { value: 'M1', label: 'M1 (Male)' },
  { value: 'M2', label: 'M2 (Male)' },
  { value: 'M3', label: 'M3 (Male)' },
  { value: 'M4', label: 'M4 (Male)' },
  { value: 'M5', label: 'M5 (Male)' },
  { value: 'andromeda', label: 'Andromeda (Custom)' },
  { value: 'arcas', label: 'Arcas (Custom)' },
  { value: 'asteria', label: 'Asteria (Custom)' },
  { value: 'electra', label: 'Electra (Custom)' },
  { value: 'sarah_eve', label: 'Sarah Eve (Custom)' },
  { value: 'veda_sky', label: 'Veda Sky (Custom)' },
  { value: 'amanda', label: 'Amanda (Custom)' },
  { value: 'charlotte', label: 'Charlotte (Custom)' },
  { value: 'bella', label: 'Bella (Custom)' },
];

const DEFAULT_CONFIG: VoiceConfig = {
  provider: USE_TENANT_CARRIER,
  accountSid: '',
  authToken: '',
  telnyxApiKey: '',
  phoneNumber: '',
  voiceConfig: {
    voice: DEFAULT_VOICE.elevenlabs,
    language: 'en-US',
    gatherTimeoutSeconds: 12,
    ttsService: 'elevenlabs',
  },
  welcomeMessage: 'Hello! How can I assist you today?',
};

const VOICE_PREVIEW_TEXT = 'Hello! This is a quick voice preview.';

const VoiceConfigTab: React.FC<Props> = ({ agent }) => {
  const [config, setConfig] = useState<VoiceConfig>(DEFAULT_CONFIG);
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [carrierOptions, setCarrierOptions] = useState<Array<{ code: string; label: string }>>([
    { code: 'twilio', label: 'Twilio' },
    { code: 'telnyx', label: 'Telnyx' },
  ]);

  // Offer exactly the carriers this build implements (falls back to Twilio/Telnyx if unavailable).
  useEffect(() => {
    voiceConfigService
      .listTelephonyProviders()
      .then((res: any) => {
        const list = Array.isArray(res?.providers) ? res.providers : [];
        if (list.length) setCarrierOptions(list.map((p: any) => ({ code: p.code, label: p.label || p.code })));
      })
      .catch(() => { /* keep the fallback list */ });
  }, []);

  const [elevenVoices, setElevenVoices] = useState<Array<{ voice_id: string; name: string }>>([]);
  const [fishVoices, setFishVoices] = useState<Array<{ id: string; name: string; languages: string[] }>>([]);
  // When true, the user is entering a voice/reference ID by hand instead of
  // picking one from the account list (ElevenLabs or Fish).
  const [customVoiceMode, setCustomVoiceMode] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [voicesWarning, setVoicesWarning] = useState('');
  const [previewingVoice, setPreviewingVoice] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Load agent config
  useEffect(() => {
    if (!agent) return;
    const meta = agent.metadata || {};
    const tc = meta.twilioConfig || {};
    const telC = meta.telephonyConfig || {};
    const savedService: TtsService = tc.voiceConfig?.ttsService || 'elevenlabs';
    setConfig({
      // An explicitly chosen carrier is kept as saved (plivo, …); none means "use the client's".
      provider: typeof telC.provider === 'string' && telC.provider ? telC.provider : USE_TENANT_CARRIER,
      accountSid: telC.accountSid || tc.accountSid || '',
      authToken: telC.authToken || tc.authToken || '',
      telnyxApiKey: telC.apiKey || '',
      phoneNumber: telC.phoneNumber || tc.phoneNumber || '',
      voiceConfig: {
        voice: tc.voiceConfig?.voice || DEFAULT_VOICE[savedService],
        language: tc.voiceConfig?.language || 'en-US',
        gatherTimeoutSeconds:
          typeof tc.voiceConfig?.gatherTimeoutSeconds === 'number'
            ? tc.voiceConfig.gatherTimeoutSeconds
            : typeof tc.voiceConfig?.timeout === 'number'
              ? tc.voiceConfig.timeout
              : typeof tc.voiceConfig?.speechTimeoutSeconds === 'number'
                ? tc.voiceConfig.speechTimeoutSeconds
                : 12,
        speechPauseTimeoutSeconds:
          typeof tc.voiceConfig?.speechPauseTimeoutSeconds === 'number'
            ? tc.voiceConfig.speechPauseTimeoutSeconds
            : undefined,
        timeout: typeof tc.voiceConfig?.timeout === 'number' ? tc.voiceConfig.timeout : undefined,
        speechTimeoutSeconds:
          typeof tc.voiceConfig?.speechTimeoutSeconds === 'number'
            ? tc.voiceConfig.speechTimeoutSeconds
            : undefined,
        ttsService: savedService,
        idleMessages: Array.isArray(tc.voiceConfig?.idleMessages) ? tc.voiceConfig.idleMessages : [],
        idleTimeoutSeconds:
          typeof tc.voiceConfig?.idleTimeoutSeconds === 'number' ? tc.voiceConfig.idleTimeoutSeconds : 10,
        idleMaxPrompts:
          typeof tc.voiceConfig?.idleMaxPrompts === 'number' ? tc.voiceConfig.idleMaxPrompts : 2,
        idleGoodbyeMessage: tc.voiceConfig?.idleGoodbyeMessage || '',
        enableHoldMusic: tc.voiceConfig?.enableHoldMusic !== false,
        enableAmbientMusic: tc.voiceConfig?.enableAmbientMusic !== false,
        ambientMusicKey: tc.voiceConfig?.ambientMusicKey || '',
        ambientMusicBucket: tc.voiceConfig?.ambientMusicBucket || '',
        keyterms: tc.voiceConfig?.keyterms || '',
      },
      welcomeMessage: tc.welcomeMessage || meta.welcomeMessage || 'Hello! How can I assist you today?',
    });
    setIsDirty(false);
  }, [agent]);

  // Fetch ElevenLabs voices whenever the service is set to elevenlabs
  useEffect(() => {
    if (config.voiceConfig.ttsService !== 'elevenlabs') return;
    setLoadingVoices(true);
    setVoicesWarning('');
    agentInstanceService.getElevenLabsVoices()
      .then((data) => {
        setElevenVoices(data.voices || []);
        setVoicesWarning(data.warning || '');
        setLoadingVoices(false);
      })
      .catch(() => {
        setElevenVoices([
          { voice_id: '21m00Tcm4TlvDq8ikWAM', name: 'Rachel' },
          { voice_id: 'EXAVITQu4vr4xnSDxMaL', name: 'Bella' },
          { voice_id: 'TxGEqnHWrfWFTfGW9XjX', name: 'Josh' },
          { voice_id: 'pNInz6obpgDQGcFmaJgB', name: 'Adam' },
        ]);
        setVoicesWarning('Could not load ElevenLabs voices. Showing fallback voices only.');
        setLoadingVoices(false);
      });
  }, [config.voiceConfig.ttsService]);

  // If the saved voice isn't one of the account voices, it's a hand-entered
  // custom ID — switch to custom mode so it displays/edits correctly.
  useEffect(() => {
    if (config.voiceConfig.ttsService !== 'elevenlabs' || !elevenVoices.length) return;
    const v = config.voiceConfig.voice;
    if (v && !elevenVoices.some((ev) => ev.voice_id === v)) setCustomVoiceMode(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elevenVoices]);

  // Fetch the Fish Audio account's voices whenever the service is set to fish.
  useEffect(() => {
    if (config.voiceConfig.ttsService !== 'fish') return;
    setLoadingVoices(true);
    setVoicesWarning('');
    agentInstanceService.getFishVoices()
      .then((data) => {
        setFishVoices(data.voices || []);
        setVoicesWarning(data.warning || '');
        setLoadingVoices(false);
      })
      .catch(() => {
        setFishVoices([]);
        setVoicesWarning('Could not load Fish Audio voices. Enter a reference ID manually.');
        setLoadingVoices(false);
      });
  }, [config.voiceConfig.ttsService]);

  // A saved Fish reference ID not in the account list is a hand-entered custom ID.
  useEffect(() => {
    if (config.voiceConfig.ttsService !== 'fish' || !fishVoices.length) return;
    const v = config.voiceConfig.voice;
    if (v && !fishVoices.some((fv) => fv.id === v)) setCustomVoiceMode(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fishVoices]);

  const CUSTOM_VOICE_OPTION = '__custom__';
  const FISH_DEFAULT_OPTION = '__fish_default__';

  const handleElevenVoiceSelect = (value: string) => {
    if (value === CUSTOM_VOICE_OPTION) {
      setCustomVoiceMode(true);
      return; // keep the current voice value; the text field lets them edit it
    }
    setCustomVoiceMode(false);
    updateVoice('voice', value);
  };

  const handleFishVoiceSelect = (value: string) => {
    if (value === CUSTOM_VOICE_OPTION) {
      setCustomVoiceMode(true);
      return;
    }
    setCustomVoiceMode(false);
    // The default-voice option maps to an empty reference_id.
    updateVoice('voice', value === FISH_DEFAULT_OPTION ? '' : value);
  };

  const update = (field: keyof VoiceConfig, value: any) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const updateVoice = (field: string, value: any) => {
    setConfig((prev) => ({
      ...prev,
      voiceConfig: { ...prev.voiceConfig, [field]: value },
    }));
    setIsDirty(true);
  };

  // Custom ambient MP3 upload
  const [uploadingAmbient, setUploadingAmbient] = useState(false);
  const ambientFileRef = useRef<HTMLInputElement>(null);

  const handleAmbientUpload = async (file?: File | null) => {
    if (!file) return;
    if (!/audio\/(mpeg|mp3)/i.test(file.type) && !/\.mp3$/i.test(file.name)) {
      toast.error('Please upload an MP3 file.');
      return;
    }
    setUploadingAmbient(true);
    try {
      const res: any = await fileService.upload(file, { subdir: 'ambient-music' });
      const f = res?.items?.[0] ?? res?.files?.[0] ?? res;
      const key = f?.key;
      const bucket = f?.bucket ?? res?.bucket;
      if (!key || !bucket) throw new Error('Upload did not return a key/bucket');
      setConfig((prev) => ({
        ...prev,
        voiceConfig: { ...prev.voiceConfig, ambientMusicKey: key, ambientMusicBucket: bucket },
      }));
      setIsDirty(true);
      toast.success('Ambient music uploaded. Save to apply.');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to upload ambient music.');
    } finally {
      setUploadingAmbient(false);
      if (ambientFileRef.current) ambientFileRef.current.value = '';
    }
  };

  // Idle-message list editors
  const idleMessages = config.voiceConfig.idleMessages ?? [];
  const setIdleMessages = (next: string[]) => updateVoice('idleMessages', next);
  const updateIdleMessage = (i: number, value: string) =>
    setIdleMessages(idleMessages.map((m, idx) => (idx === i ? value : m)));
  const addIdleMessage = () => setIdleMessages([...idleMessages, '']);
  const removeIdleMessage = (i: number) => setIdleMessages(idleMessages.filter((_, idx) => idx !== i));

  const handlePreviewVoice = async () => {
    if (!isElevenLabs && !isSupertonic && !isFish) return;
    // Fish can preview with an empty reference_id (uses its default voice); the
    // other providers require a voice to be selected first.
    if (!config.voiceConfig.voice && !isFish) {
      toast.error('Select a voice first.');
      return;
    }

    setPreviewingVoice(true);
    try {
      const blob = await agentInstanceService.previewElevenLabsVoice({
        voiceId: config.voiceConfig.voice,
        text: VOICE_PREVIEW_TEXT,
        ...(isSupertonic ? { provider: 'supertonic' } : isFish ? { provider: 'fish' } : {}),
      });

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }

      const objectUrl = URL.createObjectURL(blob);
      const audio = new Audio(objectUrl);
      audioRef.current = audio;
      audio.onended = () => URL.revokeObjectURL(objectUrl);
      audio.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        // Superseded by a later preview (e.g. its src was reset to stop it) —
        // that's not a real playback failure, so don't surface a toast for it.
        if (audioRef.current !== audio) return;
        setPreviewingVoice(false);
        toast.error(
          isSupertonic
            ? 'Voice preview failed. Check that the Supertonic sidecar is running and reachable.'
            : isFish
              ? 'Voice preview failed. Check that FISH_API_KEY is configured and the voice (reference) ID is valid.'
              : 'Voice preview failed. Check that ElevenLabs API key is configured and the voice ID is valid.',
        );
      };
      await audio.play();
    } catch (error: any) {
      const message = error?.message || 'Failed to play voice preview.';
      toast.error(message);
    } finally {
      setPreviewingVoice(false);
    }
  };

  const handleServiceChange = (service: TtsService) => {
    setCustomVoiceMode(false);
    setConfig((prev) => ({
      ...prev,
      voiceConfig: { ...prev.voiceConfig, ttsService: service, voice: DEFAULT_VOICE[service] },
    }));
    setIsDirty(true);
  };

  const validate = () => {
    if (config.provider === 'telnyx') {
      if (!config.telnyxApiKey.trim()) return 'Telnyx API Key is required.';
    } else if (config.provider === 'twilio') {
      if (!config.accountSid.trim()) return 'Twilio Account SID is required.';
      if (!config.authToken.trim()) return 'Twilio Auth Token is required.';
    }
    if (config.voiceConfig.gatherTimeoutSeconds < 5 || config.voiceConfig.gatherTimeoutSeconds > 60) return 'Timeout must be between 5 and 60 seconds.';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    if (!agent?.id) { toast.error('Agent ID missing.'); return; }

    setSaving(true);
    try {
      const parsedVersion = Number(agent.version);
      const currentVersion = Number.isFinite(parsedVersion) && parsedVersion > 0 ? parsedVersion : undefined;
      const currentMeta = agent.metadata || {};
      const {
        gatherTimeoutSeconds,
        speechPauseTimeoutSeconds,
        timeout,
        speechTimeoutSeconds,
        ...voiceConfigRest
      } = config.voiceConfig;
      const payload = {
        metadata: {
          ...currentMeta,
          // twilioConfig keeps the voiceConfig + Twilio creds the runtime already reads.
          twilioConfig: {
            accountSid: config.accountSid.trim(),
            authToken: config.authToken.trim(),
            phoneNumber: config.phoneNumber.trim(),
            voiceConfig: {
              ...voiceConfigRest,
              gatherTimeoutSeconds,
              timeout: gatherTimeoutSeconds,
              ...(typeof speechPauseTimeoutSeconds === 'number' ? { speechPauseTimeoutSeconds } : {}),
            },
            welcomeMessage: config.welcomeMessage,
          },
          // telephonyConfig.provider overrides the client's carrier for this agent — written only
          // when one is explicitly chosen. Only Twilio/Telnyx carry agent-level credentials; other
          // carriers authenticate with the client's own credentials.
          telephonyConfig: {
            ...(config.provider !== USE_TENANT_CARRIER ? { provider: config.provider } : {}),
            phoneNumber: config.phoneNumber.trim(),
            ...(config.provider === 'telnyx'
              ? { apiKey: config.telnyxApiKey.trim() }
              : config.provider === 'twilio'
                ? { accountSid: config.accountSid.trim(), authToken: config.authToken.trim() }
                : {}),
          },
        },
        ...(currentVersion ? { version: currentVersion } : {}),
      };
      await agentInstanceService.updateAgentInstance(encodeURIComponent(agent.id), payload);
      toast.success('Voice configuration saved successfully.');
      setIsDirty(false);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save voice configuration.');
    } finally {
      setSaving(false);
    }
  };

  const isElevenLabs = config.voiceConfig.ttsService === 'elevenlabs';
  const isSupertonic = config.voiceConfig.ttsService === 'supertonic';
  const isFish = config.voiceConfig.ttsService === 'fish';

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Voice Configuration</h2>
          <p className="text-sm text-gray-500 mt-1">
            Configure Twilio credentials and voice settings. Phone number and webhook URLs are configured per Ecosystem in the Connect tab.
          </p>
        </div>
        {isDirty && (
          <Badge className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full border border-amber-200">
            Unsaved changes
          </Badge>
        )}
      </div>

      {/* Telephony Credentials */}
      <Surface tone="card" as="section" className="rounded-xl border p-6 space-y-5">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Telephony Credentials</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Telephony Provider</label>
          <SelectField
            value={config.provider}
            onChange={(e) => update('provider', e.target.value as TelephonyProvider)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={USE_TENANT_CARRIER}>Use client's carrier (Settings → Voice)</option>
            {carrierOptions.map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
            {config.provider !== USE_TENANT_CARRIER && !carrierOptions.some((c) => c.code === config.provider) && (
              <option value={config.provider}>{config.provider} (not supported by this build)</option>
            )}
          </SelectField>
          <p className="text-xs text-gray-400 mt-1">
            The carrier that powers inbound/outbound calls for this agent. Leave it on the client's carrier unless this
            agent must use a different one.
          </p>
        </div>

        {config.provider === 'twilio' ? (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account SID</label>
              <TextField
                type="text"
                value={config.accountSid}
                onChange={(e) => update('accountSid', e.target.value)}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              />
              <p className="text-xs text-gray-400 mt-1">Found in your Twilio Console dashboard.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Auth Token</label>
              <div className="relative">
                <TextField
                  type={showAuthToken ? 'text' : 'password'}
                  value={config.authToken}
                  onChange={(e) => update('authToken', e.target.value)}
                  placeholder="••••••••••••••••••••••••••••••••"
                  className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                />
                <Button tone="custom"
                  type="button"
                  onClick={() => setShowAuthToken(!showAuthToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-700"
                >
                  {showAuthToken ? 'Hide' : 'Show'}
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Stored securely. Never shared with the frontend.</p>
            </div>
          </>
        ) : config.provider === 'telnyx' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telnyx API Key</label>
            <div className="relative">
              <TextField
                type={showAuthToken ? 'text' : 'password'}
                value={config.telnyxApiKey}
                onChange={(e) => update('telnyxApiKey', e.target.value)}
                placeholder="KEYxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              />
              <Button tone="custom"
                type="button"
                onClick={() => setShowAuthToken(!showAuthToken)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-700"
              >
                {showAuthToken ? 'Hide' : 'Show'}
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-1">V2 API key from your Telnyx Mission Control portal. Used for Call Control (transfer) and streaming.</p>
          </div>
        ) : null}
        {config.provider === USE_TENANT_CARRIER ? (
          <p className="text-sm text-gray-600">
            Calls use the carrier and credentials configured for this client under{' '}
            <span className="font-medium">All Clients → client → Settings → Voice</span>.
          </p>
        ) : !AGENT_LEVEL_CREDENTIAL_PROVIDERS.has(config.provider) && (
          <p className="text-sm text-gray-600">
            {(carrierOptions.find((c) => c.code === config.provider)?.label ?? config.provider)} credentials are configured once per
            client under <span className="font-medium">All Clients → client → Settings → Voice</span>. The phone number comes from
            the workflow's trigger, or that client's default number.
          </p>
        )}
      </Surface>

      {/* Voice Settings */}
      <Surface tone="card" as="section" className="rounded-xl border p-6 space-y-5">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Voice Settings</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* TTS Service */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">TTS Service</label>
            <SelectField
              value={config.voiceConfig.ttsService}
              onChange={(e) => handleServiceChange(e.target.value as TtsService)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="elevenlabs">ElevenLabs</option>
              <option value="supertonic">Supertonic (self-hosted)</option>
              <option value="fish">Fish Audio</option>
              <option value="twilio">Twilio (Built-in)</option>
            </SelectField>
            <p className="text-xs text-gray-400 mt-1">
              {isElevenLabs
                ? 'Uses ElevenLabs API for high-quality TTS.'
                : isSupertonic
                  ? 'Self-hosted, on-device TTS — no per-use cost or quota. Slightly higher latency per sentence.'
                  : isFish
                    ? 'Uses Fish Audio (fish.audio) for high-quality TTS. Paste a voice reference ID from fish.audio, or leave blank for the default voice.'
                    : "Uses Twilio's built-in Polly voices."}
            </p>
          </div>

          {/* TTS Voice */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isElevenLabs ? 'ElevenLabs Voice' : isSupertonic ? 'Supertonic Voice' : isFish ? 'Fish Audio Voice' : 'Twilio Voice'}
            </label>

            {isElevenLabs ? (
              loadingVoices ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-400 bg-gray-50">
                  Loading voices…
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="flex gap-2">
                  <SelectField
                    value={customVoiceMode ? CUSTOM_VOICE_OPTION : config.voiceConfig.voice}
                    onChange={(e) => handleElevenVoiceSelect(e.target.value)}
                    className="min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {elevenVoices.length === 0 && !customVoiceMode && (
                      <option value={config.voiceConfig.voice}>{config.voiceConfig.voice}</option>
                    )}
                    {elevenVoices.map((v) => (
                      <option key={v.voice_id} value={v.voice_id}>{v.name}</option>
                    ))}
                    <option value={CUSTOM_VOICE_OPTION}>➕ Custom Voice ID…</option>
                  </SelectField>
                  <Button tone="outline-white"
                    type="button"
                    onClick={handlePreviewVoice}
                    disabled={loadingVoices || previewingVoice || !config.voiceConfig.voice}
                    className="shrink-0 inline-flex items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Play voice preview"
                    title="Play voice preview"
                  >
                    {previewingVoice ? 'Playing...' : 'Play'}
                  </Button>
                  </div>
                  {customVoiceMode && (
                    <div>
                      <TextField
                        type="text"
                        value={config.voiceConfig.voice}
                        onChange={(e) => updateVoice('voice', e.target.value.trim())}
                        placeholder="Paste ElevenLabs Voice ID (e.g. 21m00Tcm4TlvDq8ikWAM)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-400">
                        Find the Voice ID in ElevenLabs → Voices → your voice → "ID". It's saved and used directly for this agent.
                      </p>
                    </div>
                  )}
                  {voicesWarning && (
                    <p className="text-xs text-amber-600">{voicesWarning}</p>
                  )}
                </div>
              )
            ) : isSupertonic ? (
              <div className="flex gap-2">
                <SelectField
                  value={config.voiceConfig.voice}
                  onChange={(e) => updateVoice('voice', e.target.value)}
                  className="min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {SUPERTONIC_VOICE_OPTIONS.map((v) => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </SelectField>
                <Button tone="outline-white"
                  type="button"
                  onClick={handlePreviewVoice}
                  disabled={previewingVoice || !config.voiceConfig.voice}
                  className="shrink-0 inline-flex items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Play voice preview"
                  title="Play voice preview"
                >
                  {previewingVoice ? 'Playing...' : 'Play'}
                </Button>
              </div>
            ) : isFish ? (
              loadingVoices ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-400 bg-gray-50">
                  Loading voices…
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <SelectField
                      value={customVoiceMode ? CUSTOM_VOICE_OPTION : (config.voiceConfig.voice || FISH_DEFAULT_OPTION)}
                      onChange={(e) => handleFishVoiceSelect(e.target.value)}
                      className="min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={FISH_DEFAULT_OPTION}>Default voice</option>
                      {fishVoices.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}{v.languages?.length ? ` (${v.languages.join(', ')})` : ''}
                        </option>
                      ))}
                      <option value={CUSTOM_VOICE_OPTION}>➕ Custom reference ID…</option>
                    </SelectField>
                    <Button tone="outline-white"
                      type="button"
                      onClick={handlePreviewVoice}
                      disabled={previewingVoice}
                      className="shrink-0 inline-flex items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label="Play voice preview"
                      title="Play voice preview"
                    >
                      {previewingVoice ? 'Playing...' : 'Play'}
                    </Button>
                  </div>
                  {customVoiceMode && (
                    <TextField
                      type="text"
                      value={config.voiceConfig.voice}
                      onChange={(e) => updateVoice('voice', e.target.value.trim())}
                      placeholder="Paste Fish Audio voice reference ID (blank = default voice)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                  {voicesWarning ? (
                    <p className="text-xs text-amber-600">{voicesWarning}</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-400">
                      {fishVoices.length
                        ? 'Voices pulled from your Fish Audio account. Choose one, or add a custom reference ID.'
                        : 'No voices found on your Fish Audio account yet. Choose “Custom reference ID” to paste one from fish.audio, or keep the default voice.'}
                    </p>
                  )}
                </div>
              )
            ) : (
              <SelectField
                value={config.voiceConfig.voice}
                onChange={(e) => updateVoice('voice', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {TWILIO_VOICE_OPTIONS.map((v) => (
                  <option key={v.value} value={v.value}>{v.label}</option>
                ))}
              </SelectField>
            )}

            {isElevenLabs && !loadingVoices && !voicesWarning && (
              <p className="text-xs text-gray-400 mt-1">Voice ID: <span className="font-mono">{config.voiceConfig.voice}</span></p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
            <SelectField
              value={config.voiceConfig.language}
              onChange={(e) => updateVoice('language', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {LANGUAGE_OPTIONS.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </SelectField>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Caller Input Timeout (seconds)
            </label>
            <TextField
              type="number"
              min={5}
              max={60}
              value={config.voiceConfig.gatherTimeoutSeconds}
              onChange={(e) => updateVoice('gatherTimeoutSeconds', Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              How long to wait for the caller to start responding before the voice turn times out. This does not affect ElevenLabs voice speed.
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Speech Keyterms <span className="text-gray-400 font-normal">(improves recognition)</span>
          </label>
          <TextArea
            rows={2}
            value={config.voiceConfig.keyterms ?? ''}
            onChange={(e) => updateVoice('keyterms', e.target.value)}
            placeholder="e.g. Bellus Academy, Poway, Temecula, cosmetology, esthetics"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-400 mt-1">
            Comma-separated domain terms (campus names, program names, proper nouns) that the transcriber should recognize reliably. Sent to Deepgram Nova-3 keyterm prompting. Leave blank to disable.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Welcome Message <span className="text-gray-400 font-normal">(inbound calls)</span>
          </label>
          <TextArea
            rows={2}
            value={config.welcomeMessage}
            onChange={(e) => update('welcomeMessage', e.target.value)}
            placeholder="Hello! How can I assist you today?"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
          <p className="text-xs text-gray-400 mt-1">This message is spoken when a caller first reaches this agent.</p>
        </div>
      </Surface>

      {/* No-response / idle prompts */}
      <Surface tone="card" as="section" className="rounded-xl border p-6 space-y-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">No-response prompts</h3>
          <p className="text-xs text-gray-400 mt-1">
            When the caller goes silent, the agent speaks one of these messages (chosen in turn). Add a few
            variations so repeats sound natural.
          </p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Idle messages</label>
          {idleMessages.length === 0 ? (
            <p className="text-xs text-gray-400">
              No messages added — the agent will use a default: “Are you still there? How can I help you today?”
            </p>
          ) : (
            idleMessages.map((msg, i) => (
              <div key={i} className="flex items-center gap-2">
                <TextField
                  type="text"
                  value={msg}
                  onChange={(e) => updateIdleMessage(i, e.target.value)}
                  placeholder="Are you still there? How can I help you today?"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Button tone="custom"
                  type="button"
                  onClick={() => removeIdleMessage(i)}
                  className="px-2.5 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                  aria-label="Remove message"
                >
                  ✕
                </Button>
              </div>
            ))
          )}
          <Button tone="link"
            type="button"
            onClick={addIdleMessage}
            className="text-sm font-medium"
          >
            + Add message
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Silence before prompting (seconds)</label>
            <TextField
              type="number"
              min={3}
              max={60}
              value={config.voiceConfig.idleTimeoutSeconds ?? 10}
              onChange={(e) => updateVoice('idleTimeoutSeconds', Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max prompts before hanging up</label>
            <TextField
              type="number"
              min={1}
              max={5}
              value={config.voiceConfig.idleMaxPrompts ?? 2}
              onChange={(e) => updateVoice('idleMaxPrompts', Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Goodbye message <span className="text-gray-400 font-normal">(spoken before hanging up)</span>
          </label>
          <TextArea
            rows={2}
            value={config.voiceConfig.idleGoodbyeMessage ?? ''}
            onChange={(e) => updateVoice('idleGoodbyeMessage', e.target.value)}
            placeholder="I haven't heard from you, so I'll let you go for now. Thanks for calling — goodbye!"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
           
            checked={config.voiceConfig.enableHoldMusic !== false}
            onChange={(e) => updateVoice('enableHoldMusic', e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm">
            <span className="block font-medium text-gray-700">Enable hold music</span>
            <span className="block text-gray-400 font-normal">
              Play hold music while the assistant is thinking. When off, the softer ambient sound keeps playing instead.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
           
            checked={config.voiceConfig.enableAmbientMusic !== false}
            onChange={(e) => updateVoice('enableAmbientMusic', e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm">
            <span className="block font-medium text-gray-700">Enable ambient music</span>
            <span className="block text-gray-400 font-normal">
              Play an ambient sound while the assistant is thinking, before it starts speaking. You can upload your own MP3 below.
            </span>
          </span>
        </label>

        {config.voiceConfig.enableAmbientMusic !== false && (
          <div className="ml-7 space-y-2">
            <label className="block text-xs font-medium text-gray-600">Custom ambient music (MP3)</label>
            <div className="flex flex-wrap items-center gap-2">
              <Button tone="custom"
                type="button"
                onClick={() => ambientFileRef.current?.click()}
                disabled={uploadingAmbient}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                {uploadingAmbient ? 'Uploading…' : 'Upload MP3'}
              </Button>
              {config.voiceConfig.ambientMusicKey ? (
                <>
                  <span className="truncate text-xs text-gray-500">{config.voiceConfig.ambientMusicKey.split('/').pop()}</span>
                  <Button tone="custom"
                    type="button"
                    onClick={() => { updateVoice('ambientMusicKey', ''); updateVoice('ambientMusicBucket', ''); }}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Remove
                  </Button>
                </>
              ) : (
                <span className="text-xs text-gray-400">Using the built-in ambient sound.</span>
              )}
            </div>
            <input
              ref={ambientFileRef}
              type="file"
              accept="audio/mpeg,.mp3"
              className="hidden"
              onChange={(e) => handleAmbientUpload(e.target.files?.[0])}
            />
          </div>
        )}
      </Surface>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button tone="blue"
          onClick={handleSave}
          disabled={saving || !isDirty}
          className="px-6 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'Saving...' : 'Save Voice Configuration'}
        </Button>
      </div>
    </div>
  );
};

export default VoiceConfigTab;
